import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  map,
  Observable,
  Subject,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { OrderService } from 'src/app/core/services/order';
import { TicketService } from 'src/app/core/services/ticket';
import { ContentDocumentLink } from 'src/app/models/content-document-link.model';
import { Order } from 'src/app/models/order.model';
import { Service } from 'src/app/models/service.model';
import { User } from 'src/app/models/user.model';
import { Permission } from 'src/app/shared/constants/permissions';
import * as fromStore from 'src/app/store/';
import { environment } from 'src/environments/environment';

export enum SupportTypeLabel {
  SECURITY = 'Security',
  SERVICE = 'Service',
  BILLING = 'Billing',
  TOB = 'TOB',
  CARE = 'Care',
  DISCONNECT = 'Disconnect',
}

@Component({
  selector: 'app-create-ticket',
  templateUrl: './create-ticket.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CreateTicketComponent
  implements OnInit, AfterContentInit, OnDestroy
{
  readonly dialogWidth = '560px';

  private fb = inject(UntypedFormBuilder);

  ticketFormGroup: UntypedFormGroup = this.fb.group({
    supportType: this.fb.control('', Validators.required),
  });

  formSubmitAttempted = signal(false);

  inlineErrors: Record<string, string | null> = {};

  createdOrder = signal<Order | null>(null);

  errorMessage = signal<string | null>(null);

  Permission = Permission;

  hasSubmissionError = signal(false);

  isTicketOptionSelected = signal(false);

  isTicketSubmitted = signal(false);

  isSubmitting = signal(false);

  private store = inject(Store<fromStore.State>);

  invoices$: Observable<ContentDocumentLink[]> = this.store.select(
    fromStore.getAllInvoiceAttachments,
  );

  areInvoicesLoading$: Observable<boolean> = this.store.select(
    fromStore.getInvoiceAttachmentsLoading,
  );

  areInvoicesLoaded$: Observable<boolean> = this.store.select(
    fromStore.getInvoiceAttachmentsLoaded,
  );

  services$: Observable<Service[]> = this.store.select(
    fromStore.getServicesInService,
  );

  areServicesLoading$: Observable<boolean> = this.store.select(
    fromStore.getServicesLoading,
  );

  areServicesLoaded$: Observable<boolean> = this.store.select(
    fromStore.getServicesLoaded,
  );

  hasNoServicesOrInvoices$: Observable<boolean> = combineLatest([
    this.services$,
    this.invoices$,
  ]).pipe(
    map(([services, invoices]) => !services?.length && !invoices?.length),
    distinctUntilChanged(),
  );

  selectedServiceId: string;

  contacts$: Observable<User[]> = this.store.select(fromStore.getContacts);

  private destroy$ = new Subject<void>();

  hideCustomerTicketNumber$: Observable<boolean> = this.store
    .select(fromStore.getCurrentUser)
    .pipe(
      map((user) => user?.Account?.Can_Set_Custom_Ticket_Number__c === false),
      distinctUntilChanged(),
    );

  disconnectReasons$: Observable<string[]> = this.store.select(
    fromStore.getDisconnectReasons,
  );

  supportTypeLabel = SupportTypeLabel;

  readonly generalInquirySupportTile = 'GeneralInquiry';

  selectedSupportTile = signal('');

  dialogRef = inject(MatDialogRef<CreateTicketComponent>);

  private readonly dialogData = inject(MAT_DIALOG_DATA);

  private ticketService = inject(TicketService);

  private orderService = inject(OrderService);

  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.dialogRef.disableClose = true;

    // ✅ Dispatch only if services or invoices are missing
    combineLatest([this.areServicesLoaded$, this.areInvoicesLoaded$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([areServicesLoaded, areInvoicesLoaded]) => {
        if (!areServicesLoaded) {
          this.store.dispatch(new fromStore.LoadServices());
        }
        if (!areInvoicesLoaded) {
          this.store.dispatch(new fromStore.LoadInvoiceAttachments());
        }
      });

    this.store
      .select(fromStore.getContactsLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadContacts());
        }
      });

    this.store.dispatch(new fromStore.LoadDisconnectReasons());

    combineLatest([
      this.areServicesLoaded$,
      this.areInvoicesLoaded$,
      this.services$,
      this.invoices$,
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(
          ([servicesLoaded, invoicesLoaded]) =>
            servicesLoaded && invoicesLoaded,
        ),
        take(1),
      )
      .subscribe(([, , services, invoices]) => {
        if (
          !services?.length &&
          !invoices?.length &&
          !this.dialogData.originPage
        ) {
          this.ticketFormGroup.controls.supportType.setValue(
            SupportTypeLabel.CARE,
          );
          this.selectedSupportTile.set(this.generalInquirySupportTile);
          this.isTicketOptionSelected.set(true);
          this.dialogRef.updateSize(this.dialogWidth, 'auto');
        }
      });
  }

  ngAfterContentInit(): void {
    if (this.dialogData.originPage) {
      this.ticketFormGroup.controls.supportType.setValue(
        this.dialogData.originPage,
      );
      this.onSupportTypeChange({ value: this.dialogData.originPage });
      this.isTicketOptionSelected.set(true);
      this.dialogRef.updateSize(this.dialogWidth, 'auto');
    }
    this.selectedServiceId = this.dialogData?.selectedServiceId || null;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  makeTicketSelection(): boolean {
    this.formSubmitAttempted.set(true);
    this.validateFields();
    if (this.ticketFormGroup.value.supportType !== '') {
      this.isTicketOptionSelected.set(true);
      this.dialogRef.updateSize(this.dialogWidth, 'auto');
    }
    return false;
  }

  validateFields(): void {
    Object.keys(this.ticketFormGroup.controls).forEach((prop) => {
      const control = this.ticketFormGroup.controls[prop];
      this.inlineErrors[prop] = control.valid
        ? null
        : 'Choose a ticket type to continue.';
    });
    this.cdr.markForCheck();
  }

  submitTicket(): void {
    this.isTicketSubmitted.set(true);
    this.dialogRef.updateSize(this.dialogWidth, 'auto');
  }

  submitNocForm(request): void {
    this.isSubmitting.set(true);

    this.ticketService
      .createNocTicket(request)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.hasSubmissionError.set(false);
          setTimeout(() => {
            this.store.dispatch(new fromStore.ClearTickets());
            this.store.dispatch(new fromStore.LoadTickets());
          }, 1000);
          this.submitTicket();
        }),
        catchError((error) => {
          this.hasSubmissionError.set(true);
          this.getErrorMessage(error);
          return EMPTY; // Prevents the stream from breaking
        }),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe();
  }

  submitCareForm(request): void {
    this.isSubmitting.set(true);

    this.ticketService
      .createCareTicket(request)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.hasSubmissionError.set(false);
          setTimeout(() => {
            this.store.dispatch(new fromStore.ClearTickets());
            this.store.dispatch(new fromStore.LoadTickets());
          }, 1000);
          this.submitTicket();
        }),
        catchError((error) => {
          this.hasSubmissionError.set(true);
          this.getErrorMessage(error);
          return EMPTY; // Prevents stream from breaking
        }),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe();
  }

  submitBillForm(request): void {
    this.isSubmitting.set(true);

    this.ticketService
      .createBillingTicket(request)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.hasSubmissionError.set(false);
          setTimeout(() => {
            this.store.dispatch(new fromStore.ClearTickets());
            this.store.dispatch(new fromStore.LoadTickets());
          }, 1000);
          this.submitTicket();
        }),
        catchError((error) => {
          this.hasSubmissionError.set(true);
          this.getErrorMessage(error);
          return EMPTY; // Prevents stream from breaking
        }),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe();
  }

  submitOrderForm(request): void {
    this.isSubmitting.set(true);

    this.orderService
      .createOrder(request)
      .pipe(
        takeUntil(this.destroy$),
        tap((dialogData) => {
          this.hasSubmissionError.set(false);
          this.createdOrder.set(dialogData);
          this.store.dispatch(new fromStore.ClearOrders());
          this.store.dispatch(new fromStore.LoadOrders());
          this.submitTicket();
        }),
        catchError((error) => {
          this.hasSubmissionError.set(true);
          this.getErrorMessage(error);
          return EMPTY; // Prevents stream from breaking
        }),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe();
  }

  closeDialog(): void {
    const ticketSubmitted = this.isTicketSubmitted();
    this.isSubmitting.set(false);
    this.hasSubmissionError.set(false);
    this.errorMessage.set(null);
    this.dialogRef.close({ ticketSubmitted });
  }

  goBack(): void {
    this.isTicketOptionSelected.set(false);
    this.isSubmitting.set(false);
    this.hasSubmissionError.set(false);
    this.errorMessage.set(null);
    this.dialogRef.updateSize(this.dialogWidth, 'auto');
  }

  onSupportTypeChange(event: Pick<MatSelectChange, 'value'>): void {
    this.validateFields();

    const selectedSupportType = event.value as string;

    if (selectedSupportType !== this.supportTypeLabel.CARE) {
      this.selectedSupportTile.set('');
    }

    if (selectedSupportType === this.supportTypeLabel.SECURITY) {
      const url = environment.iscUrl;

      // Open in a new tab
      window.open(url, '_blank', 'noopener,noreferrer');

      // Reset selection so user doesn't proceed into your internal flow
      this.ticketFormGroup.patchValue(
        { supportType: '' },
        { emitEvent: false },
      );
      this.inlineErrors.supportType = null;
      this.cdr.markForCheck();
      this.closeDialog();
    }
  }

  private getErrorMessage(error: any): void {
    if (Array.isArray(error?.errors)) {
      this.errorMessage.set(error.errors.join('\n'));
    } else {
      this.errorMessage.set(
        error?.message ||
          'Server Error: Unable to submit the ticket. Please try again!',
      );
    }
  }
}
